import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { contactInput, CasesAdminService, deleteContactInput } from '../../../services/cases-admin.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, filter, map, retry, startWith, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { LoginService } from 'src/app/services/login.service';
import { BrazilStateOutputDTO, CepService } from 'src/app/services/cep.service';

@Component({
  selector: 'app-register-case',
  templateUrl: './register-case.component.html',
  styleUrls: ['./register-case.component.scss']
})
export class RegisterCaseComponent implements OnInit {

  leadForm!: FormGroup;
  caseId?: number = null;

  primaryContact = [
    'WhatsApp',
    'Celular'
  ];

  contactType = [
    'Email',
    'WhatsApp',
    'Celular',
  ];
  
  benefitType = [
    'Aposentadoria por Invalidez', 
    'Auxílio por Incapacidade Temporária',
    'BPC/LOAS',
    'Outro',
  ];

  inssStatus = [
    'Nunca solicitou benefício',
    'Teve benefício indeferido',
    'Está em recurso',
    'Já teve concessão, mas deseja revisão',
    'Não sabe informar'
  ];

  brazilStates: BrazilStateOutputDTO[] = [];
  brazilStateCities: { nome: string }[] = [];
  citiesFiltered$: Observable<{ nome: string }[]>;

  isInitializingForm: boolean = false;
  loadingEditMode: boolean = false;

  hasMedicalDocumentItem = {
    hasMedicalDocument: [
      {text: 'Sim', value: true},
      {text: 'Não', value: false}
    ],
    documentHasCid : [
      {text: 'Sim', value: true},
      {text: 'Não', value: false},
      {text: 'Não informado', value: null},
    ]
  };

  isBpcItem = {
    isBpc: [
      {text: 'Sim', value: true},
      {text: 'Não', value: false},
      {text: 'Não se aplica', value: null}
    ],
    isCadUnico : [
      {text: 'Sim', value: true},
      {text: 'Não', value: false},
      {text: 'Não sabe informar', value: null},
    ]
  };

  periodRequirement = [
    {text: 'Sim', value: true},
    {text: 'Não', value: false},
    {text: 'Em dúvida / Precisa analisar caso', value: null},
  ];

  contacts: contactInput[] = [];
  deleteContactIdlist: number[] = [];
  createOrUpdateBtnDisabled: boolean = false;

  constructor(
    private readonly form: FormBuilder,
    private readonly service: CasesAdminService,
    private readonly snack: MatSnackBar,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly loginService: LoginService,
    private readonly cepService: CepService,
  ) { }

  ngOnInit(): void {
    this.initiateForm();
    this.addContact({ contactMethod: '', contact: '', primaryContact: true });
    this.contacts.forEach(contact => this.addContact(contact));
    this.stateValueListen();
    this.cityAutocompleteListen();

    this.getLeadIdParam().subscribe(id => {
      if(id !== 0){
        this.searchCurrentLead(id);
      } else {
        this.getAllBrazilianStates();
      }
    });
  }

  createOrUpdate(): void {
    this.createOrUpdateBtnDisabled = true;
    if(this.caseId){
      this.updateCase();
    } else {
      this.submitForm();
    }
  }

  onCitySelected(cityName: string) {
    this.leadForm.get('city')!.setValue(cityName);
  }

  getAllStatesCity(uf: string, editSelectedCity?: string):void {
    const cityForm = this.leadForm.get('city');
    cityForm.disable();
    cityForm.setValue('');

    this.cepService.getBrazillianStatesCity(uf)
      .pipe(
        retry(3),
        tap((response: {nome: string}[]) => {
          if(!response){
            throw new Error(`Resposta inválida.`);
          }

          this.brazilStateCities = response;
        })
      )
      .subscribe({
        next: () => {
          cityForm.enable();
          if (editSelectedCity){
            cityForm.setValue(editSelectedCity);
          }
        },
        error: (err) => {
          cityForm.disable();
          this.snack.open(err?.error?.message ?? `Erro ao buscar cidades do estado`, 'OK', { duration: 3000 });
        }
      })
  }

  getAllBrazilianStates(): void {
    const stateForm = this.leadForm.get('state');
    stateForm.disable();

    this.cepService.getBrazillianStateList()
      .pipe(
        retry(3),
        tap((response: BrazilStateOutputDTO[]) => {
          if(!response){
            throw new Error(`Resposta inválida.`);
          }

          this.brazilStates = response;
        })
      ).subscribe({
        next: () => {
          stateForm.enable();
        },
        error: (err) => {
          this.snack.open(err?.error?.message ?? `Erro ao buscar Estados`, 'OK', { duration: 3000 });
        }
      })
  }

  private updateCase(): void {
    if(!this.leadForm.valid){
      this.createOrUpdateBtnDisabled = false;
      return;
    };

    const contacts = this.leadForm.get('contacts').value;
    const cityInput = this.leadForm.get('city').value;
    const benefitType = this.leadForm.get('benefitType').value;
    const hasMedicalDocument = this.leadForm.get('hasMedicalDocument');
    const documentHasCid = this.leadForm.get('documentHasCid');
    const isBpc = this.leadForm.get('isBpc');
    const isCadUnico = this.leadForm.get('isCadUnico');
    const validateCityInput = this.brazilStateCities.some(city => city.nome === cityInput); 

    if(cityInput && !validateCityInput){
      this.leadForm.controls.city.setErrors({invalid: true});
      this.createOrUpdateBtnDisabled = false;    
      return;
    }

    if(!hasMedicalDocument.value){
      documentHasCid.setValue(null);
    } 
    
    if (!isBpc.value) {
      isCadUnico.setValue(null);
    }

    if(benefitType !== 'Outro'){
      this.leadForm.get('benefitDescription').setValue('');
    }

    const body = this.createOrUpdateStructure();
    body.casePatient.contacts = contacts;

    this.service.updateCase(this.caseId, body).subscribe({
      next: response => {
        if(response.status === 200){

          if(this.deleteContactIdlist.length > 0){

              const inputContact: deleteContactInput = {
                caseId: this.caseId,
                contactId: this.deleteContactIdlist,
              };

              this.service.deleteContact(inputContact).subscribe();
          }

          this.snack.open(response.message, 'OK', { duration: 3000 });
          this.router.navigate(['/cases-admin']);
          this.createOrUpdateBtnDisabled = false;
        };
      },
      error: () => {
        this.snack.open(`Não foi possível salvar os dados do caso. Tente novamente mais tarde.`, 'OK', { duration: 3000 });
        this.createOrUpdateBtnDisabled = false;
      }
    })

  }

  private submitForm(): void {
    if(!this.leadForm.valid){
      this.createOrUpdateBtnDisabled = false;    
      return;
    };

    const cityInput = this.leadForm.get('city').value;
    const benefitType = this.leadForm.get('benefitType').value;
    const hasMedicalDocument = this.leadForm.get('hasMedicalDocument');
    const documentHasCid = this.leadForm.get('documentHasCid');
    const isBpc = this.leadForm.get('isBpc');
    const isCadUnico = this.leadForm.get('isCadUnico');

    if(!hasMedicalDocument.value){
      documentHasCid.setValue(null);
    } 
    
    if (!isBpc.value) {
      isCadUnico.setValue(null);
    }

    if(benefitType !== 'Outro'){
      this.leadForm.get('benefitDescription').setValue('');
    }

    const validateCityInput = this.brazilStateCities.some(city => city.nome === cityInput); 

    if(cityInput && !validateCityInput){
      this.leadForm.controls.city.setErrors({invalid: true});
      this.createOrUpdateBtnDisabled = false;    
      return;
    }

    const contacts = this.leadForm.get('contacts').value;

    const body = this.createOrUpdateStructure();
    body.casePatient.contacts = contacts;

    this.service.createCase(body).subscribe({
      next: response => {
        if(response.status === 200){
          this.snack.open( response.message, 'OK', { duration: 3000 });
          this.router.navigate(['/cases-admin']);
          this.createOrUpdateBtnDisabled = false;
        };
      },
      error: () => {
        this.snack.open(`Não foi possível salvar os dados do caso. Tente novamente mais tarde.`, 'OK', { duration: 3000 });
        this.createOrUpdateBtnDisabled = false;
      }
    });
  }

  async onChangeCPF() {
    const document = this.leadForm.controls.document.value;

    if (!this.loginService.isCpfPatternValid(document) && document.length > 1) {
      this.leadForm.controls.document.setErrors({
        invalid: true
      })
      return;
    }
  }

  private createOrUpdateStructure(){
    const documentValue = this.leadForm.get('document').value;
    const userName = this.leadForm.get('name').value;
    const patientCity = this.leadForm.get('city').value;
    const patientState = this.leadForm.get('state').value;
      return {
        "benefitType": this.leadForm.get('benefitType').value,
        "benefitDescription": this.leadForm.get('benefitDescription').value,
        "inssStatus": this.leadForm.get('inssStatus').value,
        "hasMedicalDocument": this.leadForm.get('hasMedicalDocument').value,
        "documentHasCid": this.leadForm.get('documentHasCid').value,
        "isBpc": this.leadForm.get('isBpc').value,
        "isCadUnico": this.leadForm.get('isCadUnico').value,
        "periodRequirement": this.leadForm.get('periodRequirement').value,
        "observation": this.leadForm.get('observation').value,
        "casePatient": {
          "name": userName.trim(),
          "city": patientCity && patientCity.length > 0 ? patientCity : null,
          "state": patientState && patientState.length > 0 ? patientState : null,
          "document": documentValue && documentValue.length > 0 ? documentValue : null,
          "contacts": null
        }
    };
  }

  private searchCurrentLead(leadId: number){
    this.isInitializingForm = true;
    this.loadingEditMode = true;
    this.contactsFormArray.clear();

    this.service.findById(leadId).subscribe({
      next: response => {
        this.caseId = response.id;
        const stateForm = this.leadForm.get('state');
        stateForm.disable();

        this.cepService.getBrazillianStateList()
          .pipe(retry(3),)
          .subscribe({
            next: (states) => {
              const patient = response.casePatientId;
              this.brazilStates = states;

              this.leadForm.patchValue({
                name: patient.name,
                document: patient.document,
                benefitType: response.benefitType,
                benefitDescription: response.benefitDescription,
                inssStatus: response.inssStatus,
                hasMedicalDocument: response.hasMedicalDocument,
                documentHasCid: response.documentHasCid,
                isBpc: response.isBpc,
                isCadUnico: response.isCadUnico,
                periodRequirement: response.periodRequirement,
                observation: response.observation,
                state: patient.state, 
                city: patient.city
              });

              if(patient.state){
                this.getAllStatesCity(response.casePatientId.state, response.casePatientId.city);
              }
              
              stateForm.enable();
              this.isInitializingForm = false;
              this.loadingEditMode = false;

              for (let item of response.casePatientId.contacts){
                const contact = {
                  id: item.id,
                  contactMethod: item.contactMethod,
                  contact: item.contact,
                  primaryContact: item.primaryContact
                };
                this.addContact(contact);
              };
            },
            error: () => {
              this.snack.open('Erro ao carregar estados', 'OK');
              this.isInitializingForm = false;
            }
          })
    }});
  }

  get contactsFormArray(): FormArray {
    return this.leadForm.get('contacts') as FormArray;
  }

  addContact(contact?: contactInput) {
    this.contactsFormArray.push(this.form.group({
      id: [ contact?.id || null ],
      contactMethod: [contact?.contactMethod || '', Validators.required],
      contact: [contact?.contact || '', Validators.required],
      primaryContact: [contact?.primaryContact || false]
    }));
  }

  removeContact(i: number){
    const group = this.contactsFormArray.at(i);
    const id = group.get('id')?.value;

    if(id){
      this.deleteContactIdlist.push(id);
    }
    
    this.contactsFormArray.removeAt(i);
  }

  goBackToCasesList() {
    this.router.navigate(['/cases-admin']);
  }

  private getLeadIdParam(): Observable<number> {
    return this.route.paramMap.pipe(
      map(params => params.get('id')),
      map(id => Number(id)),
    );
  }

  private filterInputStringCities(value: string): { nome: string }[] {
    const valueNormalized = this.normalizeCityInput(value);

    return this.brazilStateCities.filter(city =>
      this.normalizeCityInput(city.nome).includes(valueNormalized)
    );
  }

  private normalizeCityInput(text: string): string {
    return text
      ?.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private initiateForm(): void {
    this.leadForm = this.form.group({
      name: ['', Validators.required],
      document: [null],
      city: [{value: null, disabled: true}],
      state: [null],
      contacts: this.form.array([]),
      benefitType: ['', Validators.required],
      benefitDescription: [''],
      inssStatus: ['', Validators.required],
      hasMedicalDocument: [''],
      documentHasCid: [''],
      isBpc: [''],
      isCadUnico: [''],
      periodRequirement: [''],
      observation: ['']
    });
  }

  private stateValueListen(): void {
    this.leadForm.get('state')!.valueChanges
      .pipe(
        filter(() => !this.isInitializingForm)
      )
      .subscribe((uf: string) => {
        if (uf){
          this.getAllStatesCity(uf);
        }
      });
  }

  private cityAutocompleteListen(): void {
    const cityForm = this.leadForm.get('city')!;
    this.citiesFiltered$ = cityForm.valueChanges
      .pipe(
        startWith(''),
        debounceTime(100),
        map(value => this.filterInputStringCities(value || '')),
      );
  }
}
